import { Link } from 'react-router-dom';
import type { ProvenanceEntry } from '@ni/shared';
import {
  KNOWLEDGE_NO_PROVENANCE,
  KNOWLEDGE_PROVENANCE_WORDS,
  provenanceLabel,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { provenanceHref, type ProvenanceOn } from '../../lib/knowledge';
import { EmptyLine } from '../ui/empty-line';

/**
 * **Where it came from**, the block itself — lifted out of the knowledge panel
 * so the organisation's record can render the same lines rather than a second
 * spelling of them (organisations-as-subjects note §2 chunk 2b).
 *
 * The head stays with the caller (the panel's is a disclosure). What the list
 * owns is the part a reader would notice if it drifted — the label, the kind
 * word, the day, and where the line points.
 *
 * `on` is the record page the lines are being read on, which is the only thing
 * an organisation changes: its record is one screen, so a note and a run both
 * point back at it rather than at a person's tabs. It is **required and has no
 * default**: `'person'` was the default and was silently wrong for every entity
 * the office has not tracked, which is most of them.
 *
 * **A line with nowhere to point is text, not a dead link** (2026-09-10). Where
 * `provenanceHref` returns null — an untracked record, and a link the office
 * made by hand, which is a fact rather than a page — the label is still printed
 * and still says what it says; only the anchor comes off. Rendering it anyway
 * was the reported failure: "A Note" on UT Austin's panel led to a 404.
 */
export function ProvenanceList({
  provenance,
  entityId,
  on,
}: {
  provenance: readonly ProvenanceEntry[];
  /** The record these lines are about — what the subject-keyed hrefs are built from. */
  entityId: string;
  on: ProvenanceOn;
}) {
  if (provenance.length === 0) {
    return <EmptyLine>{KNOWLEDGE_NO_PROVENANCE}</EmptyLine>;
  }

  return (
    <>
      {provenance.map((line) => {
        const href = provenanceHref(line, entityId, on);
        return (
          <div className="knowledge-source" key={`${line.kind}-${line.href_id}`}>
            {href === null ? (
              <span>{provenanceLabel(line)}</span>
            ) : (
              <Link className="link" to={href}>
                {provenanceLabel(line)}
              </Link>
            )}
            <span className="src">
              {[KNOWLEDGE_PROVENANCE_WORDS[line.kind], formatDay(line.at)].join(' · ')}
            </span>
          </div>
        );
      })}
    </>
  );
}
